import React from "react";
// @ts-ignore
import { EntityFormCoverage } from "../../clients/ClientFormCoverage";
import { useEditWorkforceCoverageAreas } from "../WorkforceInfoView/components/hooks/useWorkForce";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";

interface WorkforceFormCoverageProps {
  workforceUid: string;
  coverageAreas?: any[];
  address?: string;
  onUpdate?: () => void | Promise<void>;
  sx?: any;
}

/**
 * Wraps EntityFormCoverage for workforce.
 * Uses onSaveCoverageAreas so saves go to PUT /workforce/:uid
 * instead of the client endpoint.
 */
const ALLOWED_COVERAGE_KEYS = [
  "id",
  "address",
  "city",
  "state",
  "country",
  "zipCode",
  "latitude",
  "longitude",
  "milesOfCoverageArea",
  "name",
  "color",
];

const sanitizeCoverageArea = (area: any) => {
  if (!area || typeof area !== "object") return area;
  const cleaned: any = {};
  ALLOWED_COVERAGE_KEYS.forEach((key) => {
    let val = area[key];
    if (key === "milesOfCoverageArea" && val === undefined && area.radius !== undefined) {
      val = area.radius;
    }
    if (val !== null && val !== undefined) {
      cleaned[key] = val;
    }
  });
  return cleaned;
};

export const WorkforceFormCoverage: React.FC<WorkforceFormCoverageProps> = ({ workforceUid, coverageAreas = [], address, onUpdate, sx = {} }) => {
  const { mutate } = useEditWorkforceCoverageAreas();
  const { showSnackbar } = useGlobalSnackbar();

  const handleSaveCoverageAreas = (_uid: string, updatedAreas: any[]): Promise<{ ok: boolean }> =>
    new Promise((resolve) => {
      const cleanedAreas = (updatedAreas || []).map(sanitizeCoverageArea);

      mutate(
        { uid: workforceUid, coverageAreas: cleanedAreas },
        {
          onSuccess: async () => {
            showSnackbar("Coverage areas updated successfully", "success");
            await onUpdate?.();
            resolve({ ok: true });
          },
          onError: (err: any) => {
            const msg = err?.response?.data?.message || err?.message || "Failed to update coverage areas";
            showSnackbar(msg, "error");
            resolve({ ok: false });
          },
        },
      );
    });

  const entityCoverageProps: any = {
    mode: "update",
    entityUid: workforceUid,
    entityAreas: coverageAreas,
    allowUpdate: true,
    sx: { p: 0, ...sx },
    onUpdate,
    onSaveCoverageAreas: handleSaveCoverageAreas,
  };

  if (address) {
    entityCoverageProps.address = address;
  }
  if (address?.trim()) {
    entityCoverageProps.initialSearchAddress = address.trim();
  }

  return <EntityFormCoverage {...entityCoverageProps} />;
};
